import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ITermsAcceptanceResponse,
  TermsAcceptanceStatusEnum,
} from 'Data/interfaces/Faturamento/ITermsAcceptance';
import { Badge, ResendLink, Wrapper } from './styles';

interface ITermsStatus {
  terms?: ITermsAcceptanceResponse | null;
  isResending?: boolean;
  // Sem onResend, o link de reenvio não aparece (ex.: usuário sem permissão).
  onResend?: () => void;
}

export type TermsTone = 'ok' | 'pending' | 'warning';

// Situação da ciência nos Termos de Uso de uma empresa do rateio (matriz ou SPE).
// Só sinaliza: o rateio vale mesmo com pedidos pendentes.
const TermsStatus: FC<ITermsStatus> = ({ terms, isResending, onResend }) => {
  const { t } = useTranslation('faturamento');

  const describe = (): { label: string; tone: TermsTone; canResend: boolean } => {
    if (!terms) {
      return { label: t('terms.notRequested'), tone: 'warning', canResend: true };
    }

    switch (terms.Status) {
      case TermsAcceptanceStatusEnum.Accepted:
        return {
          label: t('terms.accepted', {
            date: terms.AcceptedAtUtc
              ? new Date(terms.AcceptedAtUtc).toLocaleDateString()
              : '',
          }),
          tone: 'ok',
          canResend: false,
        };
      case TermsAcceptanceStatusEnum.Locked:
        return { label: t('terms.locked'), tone: 'warning', canResend: true };
      default:
        if (terms.IsExpired) {
          return { label: t('terms.expired'), tone: 'warning', canResend: true };
        }
        if (!terms.EmailSentAtUtc) {
          return { label: t('terms.notSent'), tone: 'warning', canResend: true };
        }
        return { label: t('terms.pending'), tone: 'pending', canResend: true };
    }
  };

  const { label, tone, canResend } = describe();

  return (
    <Wrapper>
      <Badge
        $tone={tone}
        title={
          terms
            ? t('terms.recipient', { name: terms.RecipientName, email: terms.RecipientEmail })
            : undefined
        }
      >
        {label}
      </Badge>
      {canResend && onResend ? (
        <ResendLink type="button" onClick={onResend} disabled={isResending}>
          {isResending ? t('terms.resending') : t('terms.resend')}
        </ResendLink>
      ) : null}
    </Wrapper>
  );
};

export default TermsStatus;
